import { createTranslator, type useTranslations } from "next-intl"
import englishMetadata from "@/translations/en/metadata.json"
type MetadataTranslator = ReturnType<typeof useTranslations<"Metadata.EmailAnalytics">>

// ---------------------------------------------------------------------------
// Email Agent Analytics — Constants
// ---------------------------------------------------------------------------

import { SENTIMENT_THRESHOLDS } from "@/lib/analytics/sentiment";
import type {
  AnalyticsConfig,
  PipelineStage,
  ReplyOutcome,
  ResolutionCategory,
} from "./types";

// ---- Default analytics config ----------------------------------------------

export const DEFAULT_ANALYTICS_CONFIG: AnalyticsConfig = {
  default_period: "this_month",
  handover_definition: "escalated_and_forwarded",
  silent_close_days: 7,
  sentiment_thresholds: {
    negative_max: SENTIMENT_THRESHOLDS.negativeMax,
    neutral_max: SENTIMENT_THRESHOLDS.neutralMax,
  },
  time_constants: {
    full_handling_min: 10,
    draft_review_min: 2,
    triage_min: 3,
    context_saving_min: 2,
    error_overhead_min: 2,
    working_hours_per_month: 160,
  },
  response_time: {
    include_agent_replies: true,
    include_forwarded: true,
    sla_threshold_hours: 24,
    weekend_days: [],
  },
};

// ---- Period options --------------------------------------------------------

export { PERIOD_OPTIONS } from "@/lib/analytics/period";

// ---- Pipeline stage labels -------------------------------------------------

// ---- Reply outcome config --------------------------------------------------

export function getREPLY_OUTCOME_CONFIG(t: MetadataTranslator): Record<
  ReplyOutcome,
  { label: string; color: string }
> {
  return {
  error: { label: t("error"), color: "#ef4444" },
  escalated: { label: t("escalated"), color: "#f97316" },
  forwarded: { label: t("forwarded"), color: "#a855f7" },
  sent: { label: t("sent"), color: "#22c55e" },
  draft: { label: t("draft"), color: "#eab308" },
  skipped: { label: t("skipped"), color: "#6b7280" },
  dry_run: { label: t("dryRun"), color: "#3b82f6" },
}
}

/**
 * Outcome priority (index 0 = highest).
 * When a single auto_reply has multiple flags, first match wins.
 */
export const OUTCOME_PRIORITY: ReplyOutcome[] = [
  "error",
  "escalated",
  "forwarded",
  "sent",
  "draft",
  "skipped",
  "dry_run",
];

// ---- Resolution quality colors ---------------------------------------------

export function getRESOLUTION_CONFIG(t: MetadataTranslator): Record<
  ResolutionCategory,
  { label: string; title: string; description: string; color: string; bg: string; text: string }
> {
  return {
  positive: {
    label: t("confirmed"),
    title: t("positive"),
    description: t("lastInteractionHadPositiveSentiment"),
    color: "#22c55e",
    bg: "bg-green-50",
    text: "text-green-700",
  },
  conditional: {
    label: t("noComplaints"),
    title: t("conditionallyPositive"),
    description: t("lastInteractionHadNeutralSentimentNoComplaints"),
    color: "#3b82f6",
    bg: "bg-blue-50",
    text: "text-blue-700",
  },
  negative: {
    label: t("dissatisfied"),
    title: t("negative"),
    description: t("lastInteractionHadNegativeSentiment"),
    color: "#ef4444",
    bg: "bg-red-50",
    text: "text-red-700",
  },
  pending: {
    label: t("pending"),
    title: t("pending"),
    description: t("lastInteractionIsStillAwaitingResponse"),
    color: "#6b7280",
    bg: "bg-gray-50",
    text: "text-gray-700",
  },
}
}

// ---- Pipeline stage colors -------------------------------------------------

export const PIPELINE_STAGE_COLORS: Record<PipelineStage, string> = {
  synced: "#3b82f6",
  classified: "#8b5cf6",
  crm_processed: "#f59e0b",
  replied: "#22c55e",
};

// ---- Activity timeline colors ----------------------------------------------

export const TIMELINE_COLORS = {
  inbound: "#3b82f6",
  sent: "#22c55e",
  drafts: "#eab308",
  handovers: "#f97316",
  errors: "#ef4444",
} as const;

// ---- Tool Usage whitelist ---------------------------------------------------

/**
 * Only external business tools shown in the Tool Usage chart.
 * Internal agent tools (assess_sentiment, human_handover, skip_reply,
 * email_forward, capture_customer_info) are excluded — they're already
 * reflected in Reply Outcomes / KPI cards.
 */
export const TOOL_USAGE_WHITELIST = new Set([
  "get_shopify_order",
  "check_calendar_availability",
  "book_calendar_meeting",
]);

// ---- Classification chart palette ------------------------------------------

export const CLASSIFICATION_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#a855f7",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#6b7280",
] as const;

// Backend analytics DTOs keep their canonical labels; UI consumers pass their locale translator.
const canonicalMetadata = createTranslator({ locale: "en", messages: englishMetadata, namespace: "Metadata.EmailAnalytics" })
export const REPLY_OUTCOME_CONFIG = getREPLY_OUTCOME_CONFIG(canonicalMetadata)
export const RESOLUTION_CONFIG = getRESOLUTION_CONFIG(canonicalMetadata)
